import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../store/store';
import { FUND_FIELDS, COMPANY_PROFILE_FIELDS } from '../../utils/constants/fields';
import { PUNCTUATION } from '../../utils/constants/notation';
import { formatPercent, formatNumber, formatNumberRange } from '../../utils/utils';
import * as Portal from '@radix-ui/react-portal';
import DetailsContactTab from './DetailsContactTab';
import ChipsList from '../common/ChipsList';

const FundDetailsPopup = observer(({ fund = {}, onClose = () => {}, contact = null }) => {
  const { utilsStore } = useStore();
  const state = useLocalObservable(() => ({
    tab: contact ? 'contact' : 'general',
    setTab: (tab = 'general') => (state.tab = tab)
  }));

  const tabs = [
    ...(contact
      ? [
          {
            name: 'Contact',
            onClick: () => state.setTab('contact'),
            isActive: state.tab === 'contact'
          }
        ]
      : []),
    {
      name: 'General information',
      onClick: () => state.setTab('general'),
      isActive: state.tab === 'general'
    },
    {
      name: 'Fund information',
      onClick: () => state.setTab('information'),
      isActive: state.tab === 'information'
    }
  ];

  const companyName =
    fund.companyName || fund.companyProfile?.[COMPANY_PROFILE_FIELDS.COMPANY_NAME.NAME];

  return (
    <Portal.Root>
      <div className="shared-popup">
        <div className="shared-fund-details-popup-body">
          <div className="header">
            <div className="tabs">
              {tabs.map((tab) => {
                return (
                  <div
                    key={tab.name}
                    className={`tab${tab.isActive ? ' active' : ''}`}
                    onClick={tab.onClick}>
                    {tab.name}
                  </div>
                );
              })}
            </div>
            <div className="close" onClick={onClose}>
              Close
            </div>
          </div>

          <div className="content">
            {state.tab === 'contact' && <DetailsContactTab contact={contact} />}
            {state.tab === 'general' && (
              <>
                {!!companyName && (
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">Private equity firm</div>
                    <div className="value">{companyName}</div>
                  </div>
                )}
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.NAME.LABEL}</div>
                  <div className="value">
                    {fund[FUND_FIELDS.NAME.NAME] || PUNCTUATION.EMPTY_VALUE}
                  </div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.VINTAGE.LABEL}</div>
                  <div className="value">
                    {fund[FUND_FIELDS.VINTAGE.NAME] || PUNCTUATION.EMPTY_VALUE}
                  </div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.VOLUME.LABEL} (€M)</div>
                  <div className="value">{formatNumber(fund[FUND_FIELDS.VOLUME.NAME])}</div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.DESCRIPTION.LABEL}</div>
                  <div className="value">{fund[FUND_FIELDS.DESCRIPTION.NAME]}</div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.INDUSTRIES.LABEL}</div>
                  <div className="value">
                    {/* {utilsStore.getOptionName(fund, FUND_FIELDS.INDUSTRIES).join(', ')} */}
                    <ChipsList
                      list={utilsStore.getOptionName(fund, FUND_FIELDS.INDUSTRIES)}
                      visibleCount={88888}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.GEOGRAPHIES.LABEL}</div>
                  <div className="value">
                    {/* {utilsStore.getOptionName(fund, FUND_FIELDS.GEOGRAPHIES).join(', ')} */}
                    <ChipsList
                      list={utilsStore.getOptionName(fund, FUND_FIELDS.GEOGRAPHIES)}
                      visibleCount={88888}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.SITUATION.LABEL}</div>
                  <div className="value">
                    {/* {utilsStore.getOptionName(fund, FUND_FIELDS.SITUATION).join(', ')} */}
                    <ChipsList
                      list={utilsStore.getOptionName(fund, FUND_FIELDS.SITUATION)}
                      visibleCount={88888}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.EQUITY.LABEL}</div>
                  <div className="value">
                    {/* {utilsStore.getOptionName(fund, FUND_FIELDS.EQUITY).join(', ')} */}
                    <ChipsList
                      list={utilsStore.getOptionName(fund, FUND_FIELDS.EQUITY)}
                      visibleCount={88888}
                    />
                  </div>
                </div>
              </>
            )}
            {state.tab === 'information' && (
              <>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.REVENUE_RANGE_MIN.LABEL} (€M)</div>
                  <div className="value">
                    {formatNumberRange(
                      fund[FUND_FIELDS.REVENUE_RANGE_MIN.NAME],
                      fund[FUND_FIELDS.REVENUE_RANGE_MAX.NAME]
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.EBITDA_RANGE_MIN.LABEL} (€M)</div>
                  <div className="value">
                    {formatNumberRange(
                      fund[FUND_FIELDS.EBITDA_RANGE_MIN.NAME],
                      fund[FUND_FIELDS.EBITDA_RANGE_MAX.NAME]
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.EBITDA_MARGIN.LABEL}</div>
                  <div className="value">{formatPercent(fund[FUND_FIELDS.EBITDA_MARGIN.NAME])}</div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.ENTERPRISE_RANGE_MIN.LABEL} (€M)</div>
                  <div className="value">
                    {formatNumberRange(
                      fund[FUND_FIELDS.ENTERPRISE_RANGE_MIN.NAME],
                      fund[FUND_FIELDS.ENTERPRISE_RANGE_MAX.NAME]
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="bullet"></div>
                  <div className="label">{FUND_FIELDS.EQUITY_RANGE_MIN.LABEL} (€M)</div>
                  <div className="value">
                    {formatNumberRange(
                      fund[FUND_FIELDS.EQUITY_RANGE_MIN.NAME],
                      fund[FUND_FIELDS.EQUITY_RANGE_MAX.NAME]
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Portal.Root>
  );
});

export default FundDetailsPopup;
